import { createContext, useContext, useLayoutEffect, useMemo } from 'react';

import { GlobalOverlayActiveContext, GlobalOverlayContext } from './GlobalOverlayContextProvider';
import { overlayCloseReasons } from './overlayCloseReasons';
import { useInteract } from './useInteract';

export const OverlayStackContext = createContext(null);

/**
 * @param {object} [options]
 * @param {boolean} [options.isBlocking] A non-blocking root (e.g. a tooltip) does not become the
 * active stack, so it can't stop an open menu from closing. It handles its own interactions
 * regardless of which stack is active.
 */
export function useOverlayStackContext(options) {
  const isBlocking = options?.isBlocking ?? true;

  // The root defines the context which child overlays reuse.
  const currentContext = useContext(OverlayStackContext);
  const globalOverlayContext = useContext(GlobalOverlayContext);
  const globalOverlayActiveContext = useContext(GlobalOverlayActiveContext);

  const { context, isRoot } = useMemo(() => {
    if (currentContext != null) return { context: currentContext, isRoot: false };

    return {
      context: {
        stack: [],
      },
      isRoot: true,
    };
  }, [currentContext]);

  useLayoutEffect(() => {
    if (isRoot === false || isBlocking === false) {
      return;
    }

    globalOverlayContext.setActiveContext(context);

    return function () {
      globalOverlayContext.removeContext(context);
    };
  }, [isRoot, isBlocking, context, globalOverlayContext]);

  function getIsHandlingInteractions() {
    return isBlocking === false || globalOverlayActiveContext === context;
  }

  // The root handles all interactions and decides what closes.
  if (isRoot === true) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useInteract({
      stack: context.stack,
      onInteractOutsideStart(event) {
        if (getIsHandlingInteractions() === false) {
          return;
        }

        const stackLength = context.stack.length;
        const lastIndex = stackLength - 1;

        const overlaysToClose = [];

        for (let index = lastIndex; index >= 0; index--) {
          const item = context.stack[index];

          // We need to check here because we close with requestAnimationFrame the state variable
          // might have changed already.
          if (item.getIsPreventClose() !== true) {
            overlaysToClose.push(context.stack[index]);
          }
        }

        // Since we handled this event we prevent bubbling. This will prevent 2,3 and 1 is the
        // useInteract itself.
        // 1 Capture (down)
        // 2 Target
        // 3 Bubble (up).
        if (getHasBlockingOverlay(overlaysToClose)) {
          event.preventDefault();
          event.stopPropagation();
        }

        // onInteractOutside handles the actual closing when the pointer up fires.
      },
      onInteractOutside(event) {
        if (getIsHandlingInteractions() === false) {
          return;
        }

        const stackLength = context.stack.length;
        const lastIndex = stackLength - 1;

        const overlaysToClose = [];

        for (let index = lastIndex; index >= 0; index--) {
          const item = context.stack[index];

          // We need to check here because we close with requestAnimationFrame the state variable
          // might have changed already.
          if (item.getIsPreventClose() !== true) {
            overlaysToClose.push(context.stack[index]);
          }
        }

        // Since we handled this event we prevent bubbling. This will prevent 2,3 and 1 is the
        // useInteract itself.
        // 1 Capture (down)
        // 2 Target
        // 3 Bubble (up).
        if (getHasBlockingOverlay(overlaysToClose)) {
          event.preventDefault();
          event.stopPropagation();
        }

        // We need this because FocusScope uses requestAnimationFrame to restore the focus. So we
        // close one and let the focus restore.
        Promise.resolve().then(async () => {
          for (const { onCloseRequest } of overlaysToClose) {
            await new Promise((resolve) => {
              requestAnimationFrame(() => {
                resolve(onCloseRequest(overlayCloseReasons.INTERACT_OUTSIDE));
              });
            });
          }
        });
      },
      onInteractInsideStart(event, interactedIndex) {
        if (getIsHandlingInteractions() === false) {
          return;
        }

        // Before anything first we get the current stack context and clone it.
        const clonedStack = [...context.stack];

        // This is needed because the reason for onInteractInsideStart might be the opening of a new
        // overlay on the same stacking context level.

        const lastIndex = clonedStack.length - 1;

        // If it's not the top most overlay.
        if (interactedIndex !== lastIndex) {
          const stackLength = clonedStack.length;
          const overlaysToClose = [];

          for (let index = stackLength - 1; index > interactedIndex; index--) {
            overlaysToClose.push(clonedStack[index]);

            // Remove from the original array.
            context.stack.splice(index, 1);
          }

          if (getHasBlockingOverlay(overlaysToClose)) {
            event.preventDefault();
            event.stopPropagation();
          }

          // We need this because FocusScope uses requestAnimationFrame to restore the focus. So
          // we close one and let the focus restore.
          Promise.resolve().then(async () => {
            for (const { onCloseRequest } of overlaysToClose) {
              await new Promise((resolve) => {
                requestAnimationFrame(() => {
                  resolve(onCloseRequest(overlayCloseReasons.INTERACT_PARENT));
                });
              });
            }
          });
        }
      },
      onInteractInside(event, index) {
        if (getIsHandlingInteractions() === false) {
          return;
        }
      },
    });
  }

  return { context, isRoot };
}

/**
 * Only swallow the interaction when an overlay that is being closed blocks outside interactions.
 * Closing only non-blocking overlays (e.g. tooltips) lets the event reach its target.
 */
function getHasBlockingOverlay(overlays) {
  return overlays.some((overlay) => {
    return overlay.getShouldBlockInteractOutside();
  });
}
